/**
 * userTemplateStore.ts
 * Persists user-uploaded/saved DOCX templates in IndexedDB (much larger limit
 * than localStorage) together with user-defined tags. Backs the "My Template"
 * section of the template picker: All | System | My Template tabs plus
 * per-template tags, rename, delete, and add-to-tag.
 *
 * Storage: IndexedDB DB `mini_printer_user_templates`, object store `templates`
 * (keyPath `id`) and `tags` (keyPath `name`).
 */

const DB_NAME = "mini_printer_user_templates";
const DB_VERSION = 1;
const STORE_TEMPLATES = "templates";
const STORE_TAGS = "tags";

export interface UserTemplate {
  /** Stable unique id (e.g. crypto.randomUUID()). */
  id: string;
  /** Display name (renameable). */
  name: string;
  /** Raw .docx bytes. */
  data: Uint8Array;
  /** User-assigned tag names. */
  tags: string[];
  /** Optional saved editor state so My Template reproduces the edited doc. */
  layoutEdits?: unknown[];
  overlays?: unknown[];
  formats?: Record<string, unknown>;
  createdAt: number;
  updatedAt: number;
}

export interface UserTag {
  name: string;
  createdAt: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not available in this environment."));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_TEMPLATES)) {
        db.createObjectStore(STORE_TEMPLATES, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORE_TAGS)) {
        db.createObjectStore(STORE_TAGS, { keyPath: "name" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
  });
}

function withStore<T>(
  storeName: string,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(storeName, mode);
        const store = tx.objectStore(storeName);
        let result: T | undefined;
        let done = false;
        const req = fn(store);
        req.onsuccess = () => {
          result = req.result;
        };
        req.onerror = () => {
          done = true;
          reject(req.error ?? new Error(`${storeName} op failed`));
        };
        tx.oncomplete = () => {
          if (!done) {
            done = true;
            resolve(result as T);
          }
          db.close();
        };
        tx.onerror = () => {
          done = true;
          reject(tx.error ?? new Error(`${storeName} tx failed`));
          db.close();
        };
        tx.onabort = () => {
          done = true;
          reject(tx.error ?? new Error(`${storeName} tx aborted`));
          db.close();
        };
      })
  );
}

/** Insert or update a template. */
export function saveUserTemplate(tpl: UserTemplate): Promise<void> {
  return withStore(STORE_TEMPLATES, "readwrite", (store) =>
    store.put({ ...tpl, data: tpl.data.slice() })
  ).then(() => undefined);
}

/** Load a single template by id (returns null if absent). */
export function loadUserTemplate(id: string): Promise<UserTemplate | null> {
  return withStore(STORE_TEMPLATES, "readonly", (store) => store.get(id) as IDBRequest<unknown>).then(
    (row) => (row ? (row as UserTemplate) : null)
  );
}

/** List all user templates. */
export function listUserTemplates(): Promise<UserTemplate[]> {
  return withStore(STORE_TEMPLATES, "readonly", (store) =>
    store.getAll() as IDBRequest<unknown>
  ).then((rows) =>
    (rows as UserTemplate[]).sort((a, b) => b.updatedAt - a.updatedAt)
  );
}

/** Rename a template (or patch tags). */
export function patchUserTemplate(
  id: string,
  patch: Partial<Pick<UserTemplate, "name" | "tags">>
): Promise<void> {
  return openDb().then(
    (db) =>
      new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_TEMPLATES, "readwrite");
        const store = tx.objectStore(STORE_TEMPLATES);
        const get = store.get(id);
        get.onsuccess = () => {
          const row = get.result as UserTemplate | undefined;
          if (!row) {
            reject(new Error("Template not found"));
            return;
          }
          const next: UserTemplate = {
            ...row,
            ...patch,
            updatedAt: Date.now(),
          };
          store.put(next);
        };
        get.onerror = () => reject(get.error ?? new Error("get failed"));
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error ?? new Error("tx failed"));
        };
        tx.onabort = () => {
          db.close();
          reject(tx.error ?? new Error("tx aborted"));
        };
      })
  );
}

/** Delete a template. */
export function deleteUserTemplate(id: string): Promise<void> {
  return withStore(STORE_TEMPLATES, "readwrite", (store) =>
    store.delete(id) as IDBRequest<unknown>
  ).then(() => undefined);
}

/* ── Tags ────────────────────────────────────────────────────────── */

/** List all user-defined tags (sorted by name). */
export function listUserTags(): Promise<UserTag[]> {
  return withStore(STORE_TAGS, "readonly", (store) =>
    store.getAll() as IDBRequest<unknown>
  ).then((rows) => (rows as UserTag[]).sort((a, b) => a.name.localeCompare(b.name)));
}

/** Add a tag (no-op if it already exists). */
export function addUserTag(name: string): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) return Promise.resolve();
  return withStore(STORE_TAGS, "readwrite", (store) =>
    store.put({ name: trimmed, createdAt: Date.now() } as UserTag) as IDBRequest<unknown>
  ).then(() => undefined);
}

/** Remove a tag from the registry AND from every template that uses it. */
export function deleteUserTag(name: string): Promise<void> {
  return openDb().then(
    (db) =>
      new Promise<void>((resolve, reject) => {
        const tx = db.transaction([STORE_TAGS, STORE_TEMPLATES], "readwrite");
        tx.objectStore(STORE_TAGS).delete(name);
        const templates = tx.objectStore(STORE_TEMPLATES);
        const all = templates.getAll();
        all.onsuccess = () => {
          const rows = all.result as UserTemplate[];
          for (const row of rows) {
            if (row.tags.includes(name)) {
              templates.put({
                ...row,
                tags: row.tags.filter((t) => t !== name),
                updatedAt: Date.now(),
              });
            }
          }
        };
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error ?? new Error("tx failed"));
        };
        tx.onabort = () => {
          db.close();
          reject(tx.error ?? new Error("tx aborted"));
        };
      })
  );
}
